import { ProductCardFlatWide } from "/product-card-flat-wide";
import { GiftWithPurchaseSection } from "_types";
import clsx from "clsx";
import { _Product, GiftItemType, useCartDrawer, useGlobalProducts } from "global-stores";
import { ChevronLeftIcon } from "icons";
import React, { FC, useCallback, useEffect, useState } from "react";
import { render } from "react-dom";
import { ScrollBar } from "scrollbar";
import { JSONParse } from "utils";

export const GiftItems: FC<{
  settings: GiftWithPurchaseSection["settings"];
  blocks: GiftItemType[];
}> = ({ settings, blocks }) => {
  const [showGiftSection, setShowGiftSection] = useState(true);
  const { cartData } = useCartDrawer(({ cartData }) => ({ cartData }));
  const { hydrated } = useGlobalProducts(({ hydrated }) => ({ hydrated }));

  const [discountProducts, setDiscountProducts] = useState<(_Product & { discount: number })[]>([]);

  const getGiftItems = useCallback(async () => {
    let giftItems = [];

    for (let i = 0; i < blocks.length; i++) {
      const {
        type,
        target,
        target_collection,
        target_products,
        receives_discount_percentage,
        receives_products,
        receives_quantity,
      } = blocks[i];

      let targetValue = 0;
      let receivedQuantity = 0;
      for (let j = 0; j < cartData?.items.length; j++) {
        const lineItem = cartData?.items[j];

        if (receives_products?.includes(lineItem.handle)) {
          receivedQuantity += lineItem.quantity;
        }

        if (target_products?.includes(lineItem.handle)) {
          targetValue += type === "price" ? lineItem?.final_line_price : lineItem?.quantity;
          continue;
        }

        const product = await window.useGlobalProducts.getState().getAsyncProduct(lineItem.handle);

        if (product?.collections?.some((c) => c?.handle === target_collection)) {
          targetValue += type === "price" ? lineItem?.final_line_price : lineItem?.quantity;
        }
      }

      const targetReached = targetValue >= target;
      const receivedReached = receivedQuantity >= receives_quantity;

      if (targetReached && !receivedReached) {
        const blockDiscountProducts = (
          await Promise.all(
            receives_products?.map((handle) =>
              window.useGlobalProducts.getState().getAsyncProduct(handle)
            )
          )
        )?.map((product) => ({
          ...product,
          discount: receives_discount_percentage,
        }));

        giftItems = [...giftItems, ...blockDiscountProducts];
      }

      setDiscountProducts((current) => (current.length !== giftItems.length ? giftItems : current));
    }
  }, [cartData?.items, blocks]);

  useEffect(() => {
    if (hydrated && blocks?.length > 0 && cartData.item_count > 0) {
      getGiftItems();
    }
  }, [hydrated, blocks?.length, cartData.item_count, getGiftItems]);

  return (
    <>
      {discountProducts?.length
        ? <>
            <div className="h-4"></div>
            <div className={clsx("-m-4 bg-[#F5E8FF] p-4", showGiftSection && "")}>
              <div className="relative">
                <header className="flex items-baseline justify-between text-lg font-semibold">
                  <div>{settings.title}</div>
                  <button
                    className="flex items-center gap-1 p-1"
                    type="button"
                    onClick={(e) => {
                      setShowGiftSection((current) => !current);
                    }}
                  >
                    <ChevronLeftIcon
                      className={clsx(
                        "h-5 w-5 transition-all ease-linear",
                        showGiftSection ? "rotate-90" : "-rotate-90"
                      )}
                    />
                  </button>
                </header>
                {showGiftSection
                  ? <div className="grid gap-6 pb-2 pt-3">
                      {discountProducts.map(({ discount, ...product }) => {
                        if (!product?.available) return null;
                        product.variants = product?.variants?.map((v) => ({
                          ...v,
                          compare_at_price: v.compare_at_price || v.price,
                          price: v.price - v.price * (discount / 100),
                        }));
                        product.selected_or_first_available_variant = {
                          ...product?.selected_or_first_available_variant,
                          compare_at_price:
                            product?.selected_or_first_available_variant?.compare_at_price ||
                            product?.selected_or_first_available_variant?.price,
                          price:
                            product?.selected_or_first_available_variant?.price -
                            product?.selected_or_first_available_variant?.price * (discount / 100),
                        };
                        product.compare_at_price = product?.compare_at_price || product?.price;
                        product.compare_at_price_min =
                          product.compare_at_price_min || product?.price_min;
                        product.price = product?.price - product?.price * (discount / 100);
                        product.price_min =
                          product?.price_min - product?.price_min * (discount / 100);

                        return (
                          <div key={product?.id} className="border-b-300 border-b pb-6">
                            <ProductCardFlatWide product={product} disableCartOpen={true} />
                          </div>
                        );
                      })}
                      <ScrollBar itemCount={discountProducts?.length} />
                    </div>
                  : null}
              </div>
            </div>
          </>
        : null}
    </>
  );
};

export function initGiftDiscounts() {
  const giftItemsRoot = document.querySelector<HTMLElement>("[data-cart-drawer-gifts]");
  const giftWithPurchaseSettings = document.querySelector<HTMLElement>("[data-gift-with-purchase]");

  if (giftItemsRoot && giftWithPurchaseSettings) {
    const { settings, blocks } = JSONParse<{
      settings: GiftWithPurchaseSection["settings"];
      blocks: GiftItemType[];
    }>(giftWithPurchaseSettings?.innerHTML);

    render(<GiftItems settings={settings} blocks={blocks} />, giftItemsRoot);
  }
}
